import { z } from 'zod'
import { DateRangeFilterSchema } from './time'
import { type TagId, TagIdSchema } from './tag'
import { type GeoHashData } from './locations/model'
import { type LatLng, LatLngBoundingBoxSchema, LatLngSchema, QuadrantIdSchema } from './locations/latLng'
import {
  type AccountStatusId,
  AccountStatusIdSchema,
  CompanyIdSchema,
  type TeamId,
  TeamIdSchema,
  type UserId,
  UserIdSchema,
  type WorkflowActionId,
  WorkflowActionIdSchema,
  type WorkflowStageId,
  WorkflowIdSchema,
  WorkflowStageIdSchema,
} from './ids'
import { type DispositionId } from './disposition'
import { CustomFieldFilterSchema, RangeFilterSchema } from './customFieldMap'
import { CustomFieldSearchResultSchema } from './customField'
import { type ContactData } from './contact'
import { type AccountData, type TagWithDate } from './account'

export const ElasticFilterSchema = z.object({
  cities: z.array(z.string()).optional(),
  states: z.array(z.string()).optional().meta({ description: 'Two letter state code' }),
  zipCodes: z.array(z.string()).optional().meta({ description: '5 digit postal code' }),
  customFields: z.array(CustomFieldSearchResultSchema).optional(),
  advancedCustomFields: CustomFieldFilterSchema.optional(),
  statusIds: z.array(AccountStatusIdSchema).optional(),
  workflowId: WorkflowIdSchema.optional(),
  workflowStageIds: z.array(WorkflowStageIdSchema).optional(),
  searchByCurrentStage: z.boolean().optional().meta({
    description:
      'Whether statusIds/workflowStageIds match only the current stage. When false, matches accounts that have ever been in the stage. Defaults to true.',
  }),
  workflowActionIds: z.array(WorkflowActionIdSchema).optional(),
  tagIds: z.array(TagIdSchema).optional(),
  teamIds: z.array(TeamIdSchema).optional(),
  userIds: z.array(UserIdSchema).optional(),
  coordinates: z.array(LatLngSchema).optional(),
  boundingBox: LatLngBoundingBoxSchema.optional(),
  quadrantId: QuadrantIdSchema.optional(),
  workflowActionCount: RangeFilterSchema.optional(),
  contactCount: RangeFilterSchema.optional(),
  lastActivityDate: DateRangeFilterSchema.optional(),
  lastActionDate: DateRangeFilterSchema.optional(),
  stageEntryDate: DateRangeFilterSchema.optional(),
  companyId: CompanyIdSchema.optional(),
})
export type ElasticFilter = z.infer<typeof ElasticFilterSchema>

export type LatestWorkflowAction = {
  actionId: WorkflowActionId
  timestamp: number
  userId: UserId
}

export type LatestTag = {
  tagId: TagId
  timestamp: number
}

export type AccountFilterData = Pick<
  AccountData,
  | 'accountId'
  | 'locationId'
  | 'statusId'
  | 'teamId'
  | 'ownerId'
  | 'location'
  | 'lastActionDate'
  | 'workflowStageId'
  | 'workflowActionId'
  | 'workflowId'
> & {
  assigned: boolean
  latlng: LatLng
  workflowActionCount: number
  latestAction?: LatestWorkflowAction
  contactCount: number
  latestContact?: Pick<ContactData, 'disposition' | 'setterId' | 'contactDate'>
  /** latest tag on the account for the current user */
  latestTag?: LatestTag
  /** all tags on the account for the current user, in tagHistory order */
  tags?: TagWithDate[]
}
export type ElasticFilterData = Required<
  Omit<
    ElasticFilter,
    | 'coordinates'
    | 'teamIds'
    | 'boundingBox'
    | 'contactCount'
    | 'lastActivityDate'
    | 'lastActionDate'
    | 'stageEntryDate'
    | 'searchByCurrentStage'
    | 'userIds'
    | 'latestContact'
    | 'workflowActionCount'
    | 'workflowId'
    | 'companyId'
    | 'quadrantId'
    | 'advancedCustomFields'
    | 'tagIds'
  >
>
export type ElasticFilterResponse = SearchResultData & {
  filters: ElasticFilterData
  total: number
}

export type SearchResultData = GridResponse | AccountResponse

export type GeoHashGridData = GeoHashData & {
  assignedCount: number
  unassignedCount: number
  statuses?: Record<AccountStatusId, number>
  dispositions?: Record<DispositionId, number>
  workflowStages?: Record<WorkflowStageId, number>
  workflowActions?: Record<WorkflowActionId, number>
  teams?: Record<TeamId, number>
  users?: Record<UserId, number>
}
export type GridResponse = {
  mode: 'grid'
  data: GeoHashGridData[]
}

export type AccountResponse = {
  mode: 'account'
  data: AccountFilterData[]
}

export type AccountMapHeatmapData = Omit<GeoHashGridData, 'assignedCount' | 'unassignedCount'>

export type HeatmapResponse = {
  mode: 'heatmap'
  data: AccountMapHeatmapData[]
}

export type PinResponse = {
  mode: 'pin'
  data: AccountFilterData[]
}

export type AccountMapResultData = HeatmapResponse | PinResponse
